import { faThumbsDown, faThumbsUp } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  ButtonGroup,
  InputLabel,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { useAtomValue } from 'jotai';
import { useMemo, useState } from 'react';

import LargeCard from '@/components/LargeCard';
import { PERMISSIONS } from '@/constants/auth.constants';
import usePermissionsCheck from '@/hooks/usePermissionsCheck';
import { caseDetailsAtom, useSubmitFeedback } from '@/pages/Case/Case.store';
import userAtom, { userIsInternalAtom } from '@/store/user.store';

const CaseFeedbackForm = () => {
  const [rating, setRating] = useState<number | null>(null);
  const [reason, setReason] = useState<string | null>(null);
  const userIsInternal = useAtomValue(userIsInternalAtom);
  const caseDetails = useAtomValue(caseDetailsAtom);
  const user = useAtomValue(userAtom);

  const feedbackData = caseDetails?.feedback;

  const [messageError, setMessageError] = useState<string | null>(null);

  const { submitFeedback } = useSubmitFeedback();

  const resetDraftFeedback = () => {
    setRating(null);
    setReason(null);
    setMessageError(null);
  };

  const hasCasesUpdatePermission = usePermissionsCheck(
    PERMISSIONS.CASES_UPDATE
  );

  const feedbackActions = useMemo(() => {
    if (rating && !feedbackData) {
      return (
        <>
          <Button
            onClick={() => {
              resetDraftFeedback();
            }}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (rating === -1 && (!reason || reason.length < 10)) {
                setMessageError('Message must be at least 10 characters');
                return;
              }
              if (caseDetails?.id) {
                submitFeedback({
                  rating,
                  message: reason || '',
                  case_id: caseDetails?.id,
                  internal: userIsInternal,
                  action_type: 'submit_feedback',
                  user_id: user?.id || null
                });
              }
            }}
            variant="contained"
            color="primary"
            disabled={
              !!feedbackData ||
              !hasCasesUpdatePermission ||
              caseDetails?.ai_status !== 'completed'
            }
          >
            Submit Feedback
          </Button>
        </>
      );
    }
    return null;
  }, [
    rating,
    reason,
    feedbackData,
    caseDetails?.id,
    submitFeedback,
    userIsInternal,
    user?.id
  ]);

  return (
    <LargeCard actions={feedbackActions} title="User Feedback">
      <div>
        <Stack spacing={1}>
          <ButtonGroup
            disabled={
              !!feedbackData ||
              !hasCasesUpdatePermission ||
              caseDetails?.ai_status !== 'completed'
            }
          >
            <Button
              variant={
                rating === 1 || feedbackData?.rating === 1
                  ? 'contained'
                  : 'outlined'
              }
              onClick={() => {
                setRating(1);
              }}
              startIcon={<FontAwesomeIcon icon={faThumbsUp} />}
              sx={{
                width: '6.5rem',
                color:
                  rating === 1 || feedbackData?.rating === 1
                    ? 'common.white'
                    : 'primary.main'
              }}
            >
              Good
            </Button>
            <Button
              variant={
                rating === -1 || feedbackData?.rating === -1
                  ? 'contained'
                  : 'outlined'
              }
              onClick={() => {
                setRating(-1);
              }}
              startIcon={<FontAwesomeIcon icon={faThumbsDown} />}
              sx={{
                color:
                  rating === -1 || feedbackData?.rating === -1
                    ? 'common.white'
                    : 'primary.main'
              }}
            >
              Not Good
            </Button>
          </ButtonGroup>
          {feedbackData?.message && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Message:
              </Typography>
              <Typography variant="body2">{feedbackData?.message}</Typography>
            </>
          )}
          {rating && !feedbackData && (
            <Box sx={{ mt: 2 }}>
              <InputLabel htmlFor="message-input">
                Message{' '}
                <span style={{ fontWeight: 'normal' }}>
                  {rating === 1 ? '(optional)' : '*'}
                </span>
              </InputLabel>
              <TextField
                onChange={(e) => {
                  setReason(e.target.value);
                  if (messageError) {
                    setMessageError(null);
                  }
                }}
                error={!!messageError}
                fullWidth
                id="message-input"
                minRows={2}
                multiline
                value={reason || ''}
              />
              <small>Minimum 10 characters</small>
            </Box>
          )}
        </Stack>
      </div>
    </LargeCard>
  );
};

// Keyed so draft state resets when switching cases or when feedback is
// submitted/removed.
const CaseFeedback = () => {
  const caseDetails = useAtomValue(caseDetailsAtom);

  // Only show feedback form if AI status is completed
  // Feedback can only be submitted once a case is completed
  if (caseDetails?.ai_status !== 'completed') {
    return <></>;
  }
  return (
    <CaseFeedbackForm
      key={`${caseDetails?.id}-${caseDetails?.feedback ? 'submitted' : 'draft'}`}
    />
  );
};

export default CaseFeedback;
